// Fixtures mirror each production context so Preview does not expose unavailable variables.
import {PrintoutType} from "crqlar-react-common/dist/models/printout/printout.model";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {
    type PrintoutVariableResolveContext,
    withPrintoutLanguageContext,
} from "../variables/printoutVariableResolver";
import {DEFAULT_PRINTOUT_LANGUAGE} from "../core/printoutDocument";

type SampleRecord = Record<string, unknown>;

const SAMPLE_LANGUAGE = "en";
const SAMPLE_LOCALE = "en-GB";
const SAMPLE_CURRENCY = "EUR";

const RESOURCE_ID = {
    activity: "sample-resource-activity",
    meetingPoint: "sample-resource-meeting-point",
    room: "sample-resource-room",
    staff: "sample-resource-staff",
    table: "sample-resource-table",
    restaurant: "sample-resource-restaurant",
} as const;

const RESOURCE_CATEGORY_ID = {
    room: "sample-resource-category-room",
    team: "sample-resource-category-team",
} as const;

const SERVICE_CATEGORY_ID = "sample-service-category";

const AGE_CATEGORY_ID = {
    adult: "sample-age-category-adult",
    child: "sample-age-category-child",
} as const;

const sampleDate = (dayOffset: number, hours: number, minutes = 0) => {
    const date = new Date();

    date.setDate(date.getDate() + dayOffset);
    date.setHours(hours, minutes, 0, 0);

    return date;
};

const sampleDay = (dayOffset: number) => {
    const date = sampleDate(dayOffset, 0);

    return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
    ].join("-");
};

const SAMPLE_ORGANIZATION: SampleRecord = {
    address: {
        addressLine1: "Sample Allee 1",
        cityName: "Sampleburg",
        countryCode: "AT",
        countryName: "Austria",
        postalCode: "6020",
        stateProv: "Tyrol",
    },
    bic: "RZTIAT22",
    commercialRegisterNumber: "123456a",
    currencyCode: SAMPLE_CURRENCY,
    guestContactPhone: "+43 512 123 456",
    iban: "AT48 3600 0000 0012 3456",
    legalName: "Sample Grand Hotel GmbH",
    name: "Sample Grand Hotel",
    primaryEmail: "sample@hotel.example",
    vatId: "ATU 12345678",
};

const SAMPLE_PROFILE: SampleRecord = {
    id: "sample-profile-alex",
    academicTitle: "Dr.",
    addresses: [{
        addressLine1: "Sample Street 12",
        cityName: "Sampleton",
        countryCode: "DE",
        countryName: "Germany",
        isPrimary: true,
        postalCode: "80331",
        stateProv: "Bavaria",
    }],
    addressGreetings: "Dear Dr. Sample",
    birthDay: "1984-04-12",
    company: {
        companyName: "Sample Travel GmbH",
        vatCode: "ATU12345678",
    },
    currentStay: {
        endDay: sampleDay(3),
        roomId: "204",
        startDay: sampleDay(1),
    },
    emails: [{email: "alex.sample@example.com", isPrimary: true}],
    familiarName: "Alex",
    firstName: "Alex",
    gender: "FEMALE",
    languageCode: SAMPLE_LANGUAGE,
    lastName: "Sample",
    letterGreetings: "Dear Dr. Sample",
    middleName: "Kim",
    nameOrdered: "Dr. Alex Sample",
    namePrefix: "Ms",
    nationality: "DE",
    phones: [{isPrimary: true, phoneNumberFull: "+49 89 123 456"}],
    // Raw food enums exercise label resolution; medical data is never printable.
    preferences: [
        {preferenceType: "CRQLAR_FOOD_ALLERGY", preferenceValue: "TREE_NUTS"},
        {preferenceType: "CRQLAR_FOOD_ALLERGY", preferenceValue: "SHELLFISH"},
        {preferenceType: "CRQLAR_FOOD_TYPE", preferenceValue: "VEGETARIAN"},
    ],
    shortname: "A. Sample",
};

const SAMPLE_RESERVATION_PROFILES: SampleRecord[] = [
    SAMPLE_PROFILE,
    {
        id: "sample-profile-sam",
        firstName: "Sam",
        lastName: "Sample",
        nameOrdered: "Sam Sample",
        preferences: [{preferenceType: "CRQLAR_FOOD_ALLERGY", preferenceValue: "MILK"}],
    },
];

const SAMPLE_USER: SampleRecord = {
    firstName: "Jamie",
    lastName: "Sample",
};

const SAMPLE_AGE_CATEGORIES: SampleRecord[] = [
    {
        id: AGE_CATEGORY_ID.adult,
        name: "Adult",
        nameLocalized: {de: "Erwachsener", en: "Adult"},
        type: "ADULT",
    },
    {
        id: AGE_CATEGORY_ID.child,
        name: "Child",
        nameLocalized: {de: "Kind", en: "Child"},
        type: "CHILD",
    },
];

const SAMPLE_RESOURCE_CATEGORIES: SampleRecord[] = [
    {
        id: RESOURCE_CATEGORY_ID.room,
        name: "Double Room",
        nameLocalized: {de: "Doppelzimmer", en: "Double Room"},
    },
    {
        id: RESOURCE_CATEGORY_ID.team,
        name: "Spa Team",
        nameLocalized: {de: "Spa-Team", en: "Spa Team"},
    },
];

const SAMPLE_ROOM_RESOURCE: SampleRecord = {
    capacity: 2,
    categoryIds: [RESOURCE_CATEGORY_ID.room],
    descriptionLocalized: {
        de: "Doppelzimmer mit Balkon und Bergblick.",
        en: "Double room with a balcony and mountain view.",
    },
    id: RESOURCE_ID.room,
    name: "Room 204",
    nameLocalized: {de: "Zimmer 204", en: "Room 204"},
};

const SAMPLE_STAFF_RESOURCE: SampleRecord = {
    categoryIds: [RESOURCE_CATEGORY_ID.team],
    id: RESOURCE_ID.staff,
    name: "Robin Sample",
    nameLocalized: {de: "Robin Sample", en: "Robin Sample"},
};

const SAMPLE_RESTAURANT_RESOURCE: SampleRecord = {
    id: RESOURCE_ID.restaurant,
    name: "Restaurant Panorama",
    nameLocalized: {de: "Restaurant Panorama", en: "Restaurant Panorama"},
};

const SAMPLE_TABLE_RESOURCE: SampleRecord = {
    capacity: 4,
    id: RESOURCE_ID.table,
    name: "Table 12",
    nameLocalized: {de: "Tisch 12", en: "Table 12"},
    parentId: RESOURCE_ID.restaurant,
};

const SAMPLE_ACTIVITY_RESOURCE: SampleRecord = {
    id: RESOURCE_ID.activity,
    name: "Alpine tennis court",
    nameLocalized: {de: "Alpiner Tennisplatz", en: "Alpine tennis court"},
};

const SAMPLE_MEETING_POINT_RESOURCE: SampleRecord = {
    id: RESOURCE_ID.meetingPoint,
    name: "Concierge desk",
    nameLocalized: {de: "Concierge-Schalter", en: "Concierge desk"},
};

const SAMPLE_RESOURCES: SampleRecord[] = [
    SAMPLE_ACTIVITY_RESOURCE,
    SAMPLE_MEETING_POINT_RESOURCE,
    SAMPLE_ROOM_RESOURCE,
    SAMPLE_STAFF_RESOURCE,
    SAMPLE_TABLE_RESOURCE,
    SAMPLE_RESTAURANT_RESOURCE,
];

const SAMPLE_SERVICE_CATEGORIES: SampleRecord[] = [{
    id: SERVICE_CATEGORY_ID,
    name: "Wellness",
    nameLocalized: {de: "Wellness", en: "Wellness"},
}];

const SAMPLE_TAX_RATES: SampleRecord[] = [{
    id: "sample-tax-rate",
    inclusive: true,
    name: "VAT",
    rate: 10,
}];

const SAMPLE_SERVICE: SampleRecord = {
    categoryIds: [SERVICE_CATEGORY_ID],
    descriptionLocalized: {
        de: "60 Minuten Ruhe: eine Aromatherapie-Massage, gefolgt von Tee in der Panorama-Lounge.",
        en: "60 minutes of quiet: an aromatherapy massage followed by tea in the panorama lounge.",
    },
    durationMinutes: 60,
    id: "sample-service",
    name: "Sample Wellness Package",
    nameLocalized: {de: "Beispiel-Wellnesspaket", en: "Sample Wellness Package"},
    ratesExpanded: [{
        description: "Includes the treatment and the use of the spa area.",
        descriptionLocalized: {
            de: "Inklusive Behandlung und Nutzung des Spa-Bereichs.",
            en: "Includes the treatment and the use of the spa area.",
        },
        endTime: sampleDate(1, 16),
        id: "sample-rate",
        name: "Sample rate",
        nameLocalized: {de: "Beispieltarif", en: "Sample rate"},
        price: {amount: 120, amountBeforeTax: 109.09, currency: SAMPLE_CURRENCY},
        startTime: sampleDate(1, 14),
        taxRateSnap: {inclusive: true, name: "VAT", rate: 10},
    }],
    shortDescriptionLocalized: {
        de: "60 Minuten Aromatherapie-Massage.",
        en: "A 60-minute aromatherapy massage.",
    },
};

const SAMPLE_OTHER_SERVICE: SampleRecord = {
    durationMinutes: 90,
    id: "sample-other-service",
    name: "Private tennis lesson",
    nameLocalized: {de: "Private Tennisstunde", en: "Private tennis lesson"},
    shortDescriptionLocalized: {
        de: "Eine private Einheit inklusive Leihausrüstung.",
        en: "A private session with rental equipment included.",
    },
};

const SAMPLE_WEEKLY_PLANNER_SERVICE: SampleRecord = {
    durationMinutes: 120,
    id: "sample-weekly-planner-service",
    name: "Guided alpine walk",
    nameLocalized: {de: "Geführte Alpenwanderung", en: "Guided alpine walk"},
    shortDescriptionLocalized: {
        de: "Eine geführte Wanderung in gemütlichem Tempo.",
        en: "A guided walk through the surrounding alpine landscape.",
    },
};

const SAMPLE_SECTOR: SampleRecord = {
    id: "sample-sector",
    name: "Sample Sector",
};

const SAMPLE_RESERVATION: SampleRecord = {
    code: "SMPL-1042",
    expectedEndDate: sampleDate(1, 16),
    expectedEndDay: sampleDay(1),
    expectedEndTime: "16:00",
    expectedStartDate: sampleDate(1, 14),
    expectedStartDay: sampleDay(1),
    expectedStartTime: "14:00",
    guestCounts: [
        {ageCategoryId: AGE_CATEGORY_ID.adult, count: 2},
        {ageCategoryId: AGE_CATEGORY_ID.child, count: 1},
    ],
    id: "sample-reservation",
    meta: {eatPeriod: "breakfast"},
    rateSnaps: [{
        name: "Sample Half Board",
        price: {amount: 289, currency: SAMPLE_CURRENCY},
        quantity: 1,
        ratePlanCode: "SMPL-HB",
        resourceName: "Room 204",
    }],
    // Linked as well as present in resourceAdditionalInfo so both resolver paths are covered.
    resourceAdditionalInfo: [{isRoom: true, roomID: RESOURCE_ID.room}],
    resourceIds: [RESOURCE_ID.room, RESOURCE_ID.staff],
    sectorId: "sample-sector",
    serviceId: "sample-service",
    status: "CONFIRMED",
};

const SAMPLE_ACCOMMODATION_RESERVATION: SampleRecord = {
    ...SAMPLE_RESERVATION,
    expectedEndDate: sampleDate(4, 11),
    expectedEndDay: sampleDay(4),
    expectedEndTime: "11:00",
    expectedStartDate: sampleDate(1, 15),
    expectedStartDay: sampleDay(1),
    expectedStartTime: "15:00",
    rateSnaps: [{
        name: "Sample Half Board",
        price: {amount: 1287, currency: SAMPLE_CURRENCY},
        quantity: 1,
        ratePlanCode: "SMPL-HB",
        resourceName: "Room 204",
    }],
    resourceIds: [RESOURCE_ID.room],
};

const SAMPLE_CANCELLATION_RESERVATION: SampleRecord = {
    ...SAMPLE_RESERVATION,
    cancellationReason: "Guest requested cancellation.",
    cancelledDate: sampleDate(0, 9, 30),
    status: "CANCELLED",
};

const SAMPLE_OTHER_RESERVATION: SampleRecord = {
    ...SAMPLE_RESERVATION,
    expectedEndDate: sampleDate(1, 11, 30),
    expectedEndTime: "11:30",
    expectedStartDate: sampleDate(1, 10),
    expectedStartTime: "10:00",
    resourceAdditionalInfo: [],
    resourceIds: [RESOURCE_ID.activity],
    serviceId: "sample-other-service",
};

const SAMPLE_WEEKLY_PLANNER_RESERVATION: SampleRecord = {
    ...SAMPLE_RESERVATION,
    expectedEndDate: sampleDate(1, 11),
    expectedEndTime: "11:00",
    expectedStartDate: sampleDate(1, 9),
    expectedStartTime: "09:00",
    resourceAdditionalInfo: [],
    resourceIds: [RESOURCE_ID.meetingPoint],
    serviceId: "sample-weekly-planner-service",
};

// Arrival dinner through departure breakfast, matching a saved floorplan meal plan.
const SAMPLE_MEAL_PERIOD_ROWS: SampleRecord[] = [
    {day: 1, period: "dinner", hour: 19},
    {day: 2, period: "breakfast", hour: 8},
    {day: 2, period: "dinner", hour: 19},
    {day: 3, period: "breakfast", hour: 8},
].map(({day, period, hour}, index) => ({
    id: `sample-meal-${day}-${period}`,
    reservation: {
        code: `SMPL-${1043 + index}`,
        expectedEndDate: sampleDate(day, hour + 2),
        expectedEndTime: `${String(hour + 2).padStart(2, "0")}:00`,
        expectedStartDate: sampleDate(day, hour),
        expectedStartTime: `${String(hour).padStart(2, "0")}:00`,
        guestCounts: SAMPLE_RESERVATION.guestCounts,
        meta: {eatPeriod: period},
        resourceIds: [RESOURCE_ID.restaurant, RESOURCE_ID.table],
        status: "CONFIRMED",
    },
    resource: SAMPLE_TABLE_RESOURCE,
    restaurant: SAMPLE_RESTAURANT_RESOURCE,
    sectorResources: [SAMPLE_TABLE_RESOURCE],
    start: sampleDate(day, hour),
}));

// Amounts as the backend computes them: price × quantity less discounts, net truncated to cents.
const SAMPLE_FOLIO_ITEMS: SampleRecord[] = [
    {
        description: "Breakfast buffet",
        id: "sample-folio-item-breakfast",
        inclusive: false,
        isFixedCharge: false,
        itemCategory: "FOOD_AND_BEVERAGE",
        price: {amount: 12, amountCents: 1200, currency: SAMPLE_CURRENCY},
        priceDescription: "Per person",
        quantity: 2,
        serviceInventoryCode: "BRK",
        taxRateSnap: {inclusive: true, name: "VAT", rate: 10},
        timeSpanEnd: sampleDate(1, 10),
        timeSpanStart: sampleDate(1, 8),
        totalAmount: {amount: 24, amountBeforeTax: 21.81, amountBeforeTaxCents: 2181, amountCents: 2400, currency: SAMPLE_CURRENCY},
    },
    {
        description: "Minibar",
        id: "sample-folio-item-minibar",
        inclusive: false,
        isFixedCharge: false,
        itemCategory: "FOOD_AND_BEVERAGE",
        price: {amount: 3.5, amountCents: 350, currency: SAMPLE_CURRENCY},
        priceDescription: "Consumption",
        quantity: 1,
        serviceInventoryCode: "MNB",
        taxRateSnap: {inclusive: true, name: "VAT", rate: 10},
        timeSpanEnd: sampleDate(1, 22),
        timeSpanStart: sampleDate(1, 21),
        totalAmount: {amount: 3.5, amountBeforeTax: 3.18, amountBeforeTaxCents: 318, amountCents: 350, currency: SAMPLE_CURRENCY},
    },
    // Like a line added in the folio dialog: no quantity until staff change it.
    {
        description: "Spa treatment",
        discounts: [{amountCentsOff: 1000}],
        id: "sample-folio-item-spa",
        inclusive: false,
        isFixedCharge: false,
        itemCategory: "SPA",
        price: {amount: 80, amountCents: 8000, currency: SAMPLE_CURRENCY},
        priceDescription: "60 min session",
        serviceInventoryCode: "SPA",
        taxRateSnap: {inclusive: true, name: "VAT", rate: 20},
        timeSpanEnd: sampleDate(2, 16),
        timeSpanStart: sampleDate(2, 15),
        totalAmount: {amount: 70, amountBeforeTax: 58.33, amountBeforeTaxCents: 5833, amountCents: 7000, currency: SAMPLE_CURRENCY},
    },
];

const SAMPLE_FOLIO: SampleRecord = {
    // Balances are whole cents, set at finalize and on payment.
    amountDue: 9750,
    amountPaid: 9750,
    amountRemaining: 0,
    billTo: {
        email: "alex.sample@example.com",
        firstName: "Alex",
        lastName: "Sample",
        phone: "+49 89 123 456",
    },
    currency: SAMPLE_CURRENCY,
    id: "sample-folio",
    name: "Sample folio 1042",
    sequenceId: "2026-000142",
    status: "PAID",
    // Both figures are required for folio tax (97.50 - 83.32 = 14.18).
    total: {amount: 97.5, amountBeforeTax: 83.32, amountBeforeTaxCents: 8332, amountCents: 9750, currency: SAMPLE_CURRENCY},
};

const SAMPLE_AGENDA_ITEMS: SampleRecord[] = [
    {
        end: sampleDate(1, 11),
        id: "sample-agenda-weekly",
        resources: [{
            id: "sample-resource-meeting-point",
            name: "Hotel lobby",
            nameLocalized: {de: "Hotellobby", en: "Hotel lobby"},
        }],
        sector: {name: "Weekly Planner", nameLocalized: {de: "Wochenplan", en: "Weekly Planner"}},
        sectorType: "WEEKLY_PLANNER",
        service: {
            name: "Guided sunrise hike",
            nameLocalized: {de: "Geführte Sonnenaufgangswanderung", en: "Guided sunrise hike"},
        },
        start: sampleDate(1, 9),
    },
    {
        end: sampleDate(1, 15),
        id: "sample-agenda-spa",
        resources: [{
            id: "sample-resource-treatment-room",
            name: "Treatment room 2",
            nameLocalized: {de: "Behandlungsraum 2", en: "Treatment room 2"},
        }],
        sector: {name: "Spa", nameLocalized: {de: "Spa", en: "Spa"}},
        sectorType: "SPA",
        service: {
            name: "Aromatherapy massage",
            nameLocalized: {de: "Aromatherapie-Massage", en: "Aromatherapy massage"},
        },
        start: sampleDate(1, 14),
    },
    {
        end: sampleDate(1, 21),
        id: "sample-agenda-restaurant",
        resources: [SAMPLE_TABLE_RESOURCE],
        sector: {
            name: "Restaurant Panorama",
            nameLocalized: {de: "Restaurant Panorama", en: "Restaurant Panorama"},
        },
        sectorType: "RESTAURANT",
        start: sampleDate(1, 19),
    },
    {
        end: sampleDate(2, 12),
        id: "sample-agenda-other",
        sector: {name: "Activities", nameLocalized: {de: "Aktivitäten", en: "Activities"}},
        sectorType: "OTHER",
        service: {name: "Kayak rental", nameLocalized: {de: "Kajakverleih", en: "Kayak rental"}},
        start: sampleDate(2, 10),
    },
];

const getBaseContext = (): PrintoutVariableResolveContext => ({
    language: SAMPLE_LANGUAGE,
    locale: SAMPLE_LOCALE,
    now: new Date(),
    organization: SAMPLE_ORGANIZATION,
    profile: SAMPLE_PROFILE,
    user: SAMPLE_USER,
});

const getReservationContext = (sectorType?: SectorType): PrintoutVariableResolveContext => {
    const isAccommodation = sectorType === SectorType.ACCOMMODATION;
    const isOther = sectorType === SectorType.OTHER;
    const isWeeklyPlanner = sectorType === SectorType.WEEKLY_PLANNER;
    const reservation = isAccommodation
        ? SAMPLE_ACCOMMODATION_RESERVATION
        : isOther
            ? SAMPLE_OTHER_RESERVATION
            : isWeeklyPlanner
                ? SAMPLE_WEEKLY_PLANNER_RESERVATION
                : SAMPLE_RESERVATION;
    const resource = isOther
        ? SAMPLE_ACTIVITY_RESOURCE
        : isWeeklyPlanner
            ? SAMPLE_MEETING_POINT_RESOURCE
            : SAMPLE_ROOM_RESOURCE;
    const service = isOther
        ? SAMPLE_OTHER_SERVICE
        : isWeeklyPlanner
            ? SAMPLE_WEEKLY_PLANNER_SERVICE
            : SAMPLE_SERVICE;
    const sectorResources = isAccommodation
        ? [SAMPLE_ROOM_RESOURCE]
        : isOther
            ? [SAMPLE_ACTIVITY_RESOURCE]
            : isWeeklyPlanner
                ? [SAMPLE_MEETING_POINT_RESOURCE]
                : [SAMPLE_TABLE_RESOURCE];

    return {
        ageCategories: SAMPLE_AGE_CATEGORIES,
        reservation,
        reservationMealPeriods: SAMPLE_MEAL_PERIOD_ROWS,
        reservationProfiles: SAMPLE_RESERVATION_PROFILES,
        reservations: [reservation],
        resource,
        resourceCategories: SAMPLE_RESOURCE_CATEGORIES,
        resourceCategory: SAMPLE_RESOURCE_CATEGORIES[0],
        resources: SAMPLE_RESOURCES,
        restaurant: SAMPLE_RESTAURANT_RESOURCE,
        sector: {...SAMPLE_SECTOR, ...(sectorType ? {sectorType} : {})},
        sectorResources,
        service,
        serviceCategories: SAMPLE_SERVICE_CATEGORIES,
        serviceCategory: SAMPLE_SERVICE_CATEGORIES[0],
        taxRates: SAMPLE_TAX_RATES,
    };
};

const getCancellationContext = (): PrintoutVariableResolveContext => ({
    ...getReservationContext(),
    reservation: SAMPLE_CANCELLATION_RESERVATION,
    reservations: [SAMPLE_CANCELLATION_RESERVATION],
});

const getFolioContext = (): PrintoutVariableResolveContext => ({
    ageCategories: SAMPLE_AGE_CATEGORIES,
    folio: SAMPLE_FOLIO,
    folioItems: SAMPLE_FOLIO_ITEMS,
    reservation: SAMPLE_RESERVATION,
    resources: SAMPLE_RESOURCES,
    sector: SAMPLE_SECTOR,
});

const getAgendaContext = (): PrintoutVariableResolveContext => ({
    ageCategories: SAMPLE_AGE_CATEGORIES,
    agendaEnd: sampleDate(4, 23, 59),
    agendaItems: SAMPLE_AGENDA_ITEMS,
    agendaStart: sampleDate(1, 0),
    reservation: SAMPLE_ACCOMMODATION_RESERVATION,
    resources: SAMPLE_RESOURCES,
    sector: SAMPLE_SECTOR,
});

export const getSamplePreviewContext = (
    printoutType?: PrintoutType | null,
    language = DEFAULT_PRINTOUT_LANGUAGE,
    sectorType?: SectorType,
): PrintoutVariableResolveContext => {
    let context: PrintoutVariableResolveContext;

    switch (printoutType) {
        case PrintoutType.RESERVATION_CONFIRMATION:
            context = {...getBaseContext(), ...getReservationContext(sectorType)};
            break;
        case PrintoutType.RESERVATION_CANCELLATION:
            context = {...getBaseContext(), ...getCancellationContext()};
            break;
        case PrintoutType.FOLIO_CONFIRMATION:
            context = {...getBaseContext(), ...getFolioContext()};
            break;
        case PrintoutType.AGENDA_HANDOUT:
            context = {...getBaseContext(), ...getAgendaContext()};
            break;
        default:
            context = {
                ...getBaseContext(),
                ...getReservationContext(sectorType),
                ...getFolioContext(),
                ...getAgendaContext(),
            };
    }

    return withPrintoutLanguageContext(context, language);
};
